import React, { useContext } from "react"
import { Div, Text } from "atomize"
import { motion } from "framer-motion"
import Card from "../Card"
import { theme } from "../settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPersonRunning } from "@fortawesome/free-solid-svg-icons"
import {
	countDistinctPartecipants,
	getLessonTypeLabel,
	getLessonTypeTileColors,
	lessonTypeHasPlayers,
} from "../utils/func.utils"
import { modalContext } from "../providers/modal.provider"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import { formatHoursAmount } from "../utils/week.utils"

// le ore di tutti i tipi di lezione, di tutti gli sport: il numero grande
// della scheda, e quello nella pillola in cima al telefono
export const sumLessonHours = lessonsTypeCount =>
	Object.values(lessonsTypeCount ?? {}).reduce(
		(sum, sport) => sum + Object.values(sport).reduce((s, h) => s + h, 0),
		0,
	)

const LessonHoursCard = ({
	lessonsTypeCount,
	periodLessons,
	summaryDate,
	getLessonType,
	getSport,
}) => {
	const Modal = useContext(modalContext)

	// per ordinare: "Privata · 2 giocatori", non "PRI 2"
	const labelOf = lessonType => getLessonTypeLabel(lessonType, getLessonType(lessonType))

	if (!lessonsTypeCount || Object.keys(lessonsTypeCount).length === 0) return null

	const totalHours = sumLessonHours(lessonsTypeCount)

	// Il tassello dice quante ore hai fatto: toccandolo si aprono proprio
	// quelle lezioni lì, quelle contate nel periodo che stai guardando. Lo
	// sport senza ID è "unknownSport" anche nel conteggio: si filtra allo
	// stesso modo, se no il tassello mostra un elenco vuoto.
	const lessonsOfType = (sportID, lessonType) =>
		(periodLessons ?? []).filter(
			lesson =>
				(lesson.sportID || "unknownSport") === sportID && lesson.lessonType === lessonType,
		)

	const openLessonsOfType = (sportID, lessonType) =>
		Modal.open("lessonTypeLessons", {
			sportID,
			lessonType,
			summaryDate,
			lessons: lessonsOfType(sportID, lessonType),
		})

	return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" justify="space-between" m={{ b: "1rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<Div
						w="40px"
						h="40px"
						rounded="circle"
						bg={theme.colors.primary_light}
						d="flex"
						align="center"
						justify="center">
						<FontAwesomeIcon icon={faPersonRunning} color={theme.colors.primary} />
					</Div>
					<Text textWeight="bold" textSize="title">
						Ore di lezione
					</Text>
				</Div>
				<Div
					p="0.25rem 0.75rem"
					rounded="circle"
					bg={theme.colors.primary}
					d="flex"
					align="center">
					<Text textWeight="bold" textColor="white" textSize="subheader">
						{formatHoursAmount(totalHours)}
					</Text>
				</Div>
			</Div>

			{Object.keys(lessonsTypeCount).map(sportID => (
				<Div key={sportID} m={{ b: "0.75rem" }}>
					<Text
						textWeight="bold"
						textSize="caption"
						textColor={theme.colors.text_light}
						textTransform="uppercase"
						m={{ b: "0.5rem" }}>
						{getSport(sportID)?.sportName}
					</Text>
					<Div d="flex" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
						{Object.keys(lessonsTypeCount[sportID])
							.sort((a, b) => {
								// le lezioni senza campo vanno in fondo
								if (!lessonTypeHasPlayers(getLessonType(a))) return 1
								if (!lessonTypeHasPlayers(getLessonType(b))) return -1
								return labelOf(a).localeCompare(labelOf(b))
							})
							.filter(lt => lessonsTypeCount[sportID][lt] > 0)
							.map(lessonType => {
								const hours = lessonsTypeCount[sportID][lessonType]
								// le persone diverse, non le presenze sommate: quanti
								// allievi hai in quel tipo di lezione
								const people = countDistinctPartecipants(
									lessonsOfType(sportID, lessonType),
								)
								const color = getLessonTypeTileColors(getLessonType(lessonType))

								return (
									<motion.div key={lessonType} whileTap={{ scale: 0.95 }}>
										<Div
											p="0.4rem 0.75rem"
											rounded="15px"
											bg={color.bg}
											d="flex"
											flexDir="column"
											cursor="pointer"
											style={{ minWidth: "80px" }}
											onClick={() => openLessonsOfType(sportID, lessonType)}>
											<Text
												textWeight="bold"
												textSize="subheader"
												textColor={color.text}>
												<LessonTypeLabel
													lessonTypeID={lessonType}
													lessonType={getLessonType(lessonType)}
													background={color.bg}
												/>
											</Text>
											<Text textSize="caption" textColor={color.text}>
												{formatHoursAmount(hours)}
												{people > 0 && ` · ${people} pers.`}
											</Text>
										</Div>
									</motion.div>
								)
							})}
					</Div>
				</Div>
			))}

			<Text textSize="caption" textColor={theme.colors.text_light}>
				Tocca un tipo di lezione per vedere le lezioni che ci stanno dentro.
			</Text>
		</Card>
	)
}

export default LessonHoursCard
